import React, { useState, useEffect } from 'react';
import { useGetRelatedPostingsLazyQuery } from '../../generated/graphql';
import FormTile from '../Tiles/FormTile/FormTile';
import moment from 'moment';
import styles from './RelatedPostings.module.scss';

interface RelatedPostingsProps {
    supplierKey: number | null;
    terminalKey: number | null;
    currentPostingKey?: number | null; // To exclude current posting from list
    onPostingClick?: (postingKey: number) => void;
    title?: string;
    subtitle?: string;
}

const RelatedPostings: React.FC<RelatedPostingsProps> = ({
    supplierKey,
    terminalKey,
    currentPostingKey,
    onPostingClick,
    title,
    subtitle
}) => {
    const [getRelatedPostings] = useGetRelatedPostingsLazyQuery();
    const [relatedPostings, setRelatedPostings] = useState<any[]>([]);
    const [allPostingsForComparison, setAllPostingsForComparison] = useState<any[]>([]);
    const [showRelatedPostings, setShowRelatedPostings] = useState(false);

    // Effect to load related postings when both supplier and terminal are provided
    useEffect(() => {
        if (supplierKey && terminalKey) {
            loadRelatedPostings(supplierKey, terminalKey);
        } else {
            setRelatedPostings([]);
            setAllPostingsForComparison([]);
            setShowRelatedPostings(false);
        }
    }, [supplierKey, terminalKey]);

    const loadRelatedPostings = async (supplierKey: number, terminalKey: number) => {
        try {
            const result = await getRelatedPostings({
                variables: { supplierKey, terminalKey }
            });
            let allPostings = result.data?.postingGetAll?.nodes || [];

            // Keep all postings for current posting determination
            setAllPostingsForComparison(allPostings);

            // Filter out the current posting for display only
            let displayPostings = allPostings;
            if (currentPostingKey) {
                displayPostings = allPostings.filter(p => p.postingKey !== currentPostingKey);
            }

            setRelatedPostings(displayPostings);
            setShowRelatedPostings(displayPostings.length > 0);
        } catch (error) {
            console.error('Failed to load related postings:', error);
            setRelatedPostings([]);
            setAllPostingsForComparison([]);
            setShowRelatedPostings(false);
        }
    };

    const handlePostingClick = (postingKey: number) => {
        if (onPostingClick) {
            onPostingClick(postingKey);
        }
    };

    const getCurrentPosting = (postings: any[]): any | null => {
        const today = moment().startOf('day');

        // Find the most recent posting that's effective on or before today
        const applicablePostings = postings.filter(posting => {
            const effectiveDate = moment(posting.effectiveDate).startOf('day');
            return today.isSameOrAfter(effectiveDate);
        });

        if (applicablePostings.length === 0) {
            return null;
        }

        // Return the posting with the latest effective date
        return applicablePostings.reduce((latest, current) => {
            const latestDate = moment(latest.effectiveDate);
            const currentDate = moment(current.effectiveDate);
            return currentDate.isAfter(latestDate) ? current : latest;
        });
    };

    const isCurrentlyActive = (posting: any): boolean => {
        const currentPosting = getCurrentPosting(allPostingsForComparison);
        return currentPosting?.postingKey === posting.postingKey;
    };

    if (!showRelatedPostings || relatedPostings.length === 0) {
        return null;
    }

    const supplierName = relatedPostings[0]?.supplier?.name;
    const terminalName = relatedPostings[0]?.terminal?.name;

    return (
        <FormTile>
            <h5 className={styles.title}>
                {title || `Recent Postings for ${supplierName} → ${terminalName}`}
            </h5>
            <div className={styles.container}>
                {relatedPostings.map((posting, index) => {
                    const isActive = isCurrentlyActive(posting);
                    return (
                        <div
                            key={posting.postingKey}
                            className={styles.postingItem}
                            onClick={() => onPostingClick && handlePostingClick(posting.postingKey)}
                        >
                            <div className={styles.postingHeader}>
                                <div>
                                    <strong>${posting.cost}</strong>
                                    {isActive && (
                                        <span className={styles.currentBadge}>
                                            CURRENT
                                        </span>
                                    )}
                                </div>
                                <div className={styles.postingMeta}>
                                    Effective: {moment(posting.effectiveDate).format('MM/DD/YYYY')}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className={styles.summary}>
                {subtitle || (onPostingClick
                    ? "Click on any posting to edit it, or continue below to create a new one."
                    : "Related postings for this supplier and terminal combination."
                )}
            </div>
        </FormTile>
    );
};

export default RelatedPostings;